import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, getArticlesByCategory } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const articles = getArticlesByCategory(currentCategory.slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: currentCategory.description,
    url: `${siteConfig.url}/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-6xl mx-auto">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb
                  items={[{ label: currentCategory.name }]}
                />
              </div>

              {/* Category Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                    <i className={`${currentCategory.icon} text-lg`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {currentCategory.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-600 ml-13">
                  {currentCategory.description}
                </p>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                      currentCategory.slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Articles Grid */}
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      slug={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      coverImage={article.coverImage}
                      category={article.category}
                      categoryName={currentCategory.name}
                      author={article.author}
                      publishedAt={article.publishedAt}
                      tags={article.tags}
                      isHot={article.isHot}
                      isNew={article.isNew}
                      viewCount={article.viewCount}
                      commentCount={article.commentCount}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}